import { useState, useMemo, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { getLiturgicalDay } from '@/lib/liturgical/calendar';
import { addDays, isSameDay } from '@/lib/liturgical/computus';
import { getColorHex, getColorName, getSeasonName, getRankName, getNavColorHex } from '@/lib/liturgical/colors';
import { useUSCCBReadings } from '@/hooks/useUSCCBReadings';
import { ReadingBlock } from '@/components/ReadingBlock';
import type { LiturgicalDay } from '@/lib/liturgical/types';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const WEEKDAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

function fmtDate(d: Date) {
  return `${WEEKDAYS[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function usccbUrl(d: Date) {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(2);
  return `https://bible.usccb.org/bible/readings/${mm}${dd}${yy}.cfm`;
}

/** Get the current calendar day at midnight local time */
function getTodayDate(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

export function TodaysMass() {
  const [offset, setOffset] = useState(0);
  // baseToday is refreshed when the component mounts or the window regains focus.
  // This ensures the displayed date can never get "stuck" on a stale day.
  const [baseToday, setBaseToday] = useState(() => getTodayDate());

  // Refresh baseToday on window focus so an overnight tab updates correctly.
  useEffect(() => {
    const handleFocus = () => setBaseToday(getTodayDate());
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const date = useMemo(() => addDays(baseToday, offset), [baseToday, offset]);
  const isToday = isSameDay(date, getTodayDate());

  const day = useMemo(() => getLiturgicalDay(date), [date]);
  const { data: readings, isLoading, error } = useUSCCBReadings(date);
  const colorHex = getColorHex(day.color);
  const navColorHex = getNavColorHex(day.color);

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      {/* Date navigation */}
      <div className="flex items-center justify-between mb-6">
        <Button variant="outline" size="sm" onClick={() => setOffset(offset - 1)} className="gap-1.5 text-stone-600 dark:text-stone-300 border-stone-200 dark:border-stone-700">
          <ChevronLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Previous</span>
          <span className="sm:hidden">Prev</span>
        </Button>
        {!isToday && (
          <Button variant="outline" size="sm" onClick={() => setOffset(0)} className="text-xs tracking-wider uppercase">
            Go to Today
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={() => setOffset(offset + 1)} className="gap-1.5 text-stone-600 dark:text-stone-300 border-stone-200 dark:border-stone-700">
          <span className="hidden sm:inline">Next</span>
          <span className="sm:hidden">Next</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Hero card — clean white, thin color bar only */}
      <div className="relative overflow-hidden rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900">
        {/* Thin vestment color bar — the only color accent */}
        <div className="h-1 w-full" style={{ backgroundColor: colorHex }} />

        <div className="px-6 pt-6 pb-7 text-center sm:px-10">
          {/* Date */}
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-400 dark:text-stone-500">
            {fmtDate(date)}
          </p>

          {/* Season — plain text badge, no fill */}
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-stone-200 dark:border-stone-700 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-stone-600 dark:text-stone-400">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: colorHex }} />
              {getSeasonName(day.season)}
            </span>
          </div>

          {/* Celebration name */}
          <h1 className="mx-auto max-w-lg text-2xl font-semibold leading-snug sm:text-3xl text-stone-900 dark:text-stone-100" style={{ fontFamily: '"Cinzel", serif' }}>
            {day.name}
          </h1>

          {/* Info badges — all plain border, no fill */}
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <span className="rounded-full border border-stone-200 dark:border-stone-700 px-3 py-1 text-[11px] font-medium text-stone-500 dark:text-stone-400">
              {getColorName(day.color)} vestments
            </span>
            {day.rank !== 'weekday' && (
              <span className="rounded-full border border-stone-200 dark:border-stone-700 px-3 py-1 text-[11px] font-medium text-stone-500 dark:text-stone-400">
                {getRankName(day.rank)}
              </span>
            )}
            {day.holyDayOfObligation && (
              <span className="rounded-full border border-stone-300 dark:border-stone-600 px-3 py-1 text-[11px] font-semibold text-stone-700 dark:text-stone-300">
                Holy Day of Obligation
              </span>
            )}
            {day.fasting === 'fast-and-abstinence' && (
              <span className="rounded-full border border-stone-200 dark:border-stone-700 px-3 py-1 text-[11px] font-medium text-stone-500 dark:text-stone-400">
                Fast &amp; Abstinence
              </span>
            )}
            {day.fasting === 'abstinence' && (
              <span className="rounded-full border border-stone-200 dark:border-stone-700 px-3 py-1 text-[11px] font-medium text-stone-500 dark:text-stone-400">
                Abstinence
              </span>
            )}
          </div>

          {day.saintInfo && (
            <p className="mt-3 text-sm italic text-stone-400 dark:text-stone-500">{day.saintInfo}</p>
          )}
        </div>
      </div>

      {/* Metadata chips — no fills, just borders */}
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <MetaChip label="Season" value={getSeasonName(day.season)} />
        <MetaChip label="Cycle" value={`${day.sundayCycle} / ${day.weekdayCycle}`} />
        <MetaChip label="Rank" value={getRankName(day.rank)} />
        <MetaChip label="Color" value={getColorName(day.color)} dot={colorHex} />
      </div>

      {/* Readings section */}
      <section className="mt-10">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-semibold text-stone-800 dark:text-stone-200" style={{ fontFamily: '"Cinzel", serif' }}>
            Readings
          </h2>
          <a href={usccbUrl(date)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[12px] text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 transition-colors">
            USCCB.org <ExternalLink className="h-3 w-3" />
          </a>
        </div>

        {isLoading && <ReadingsSkeleton />}

        {!isLoading && readings && readings.readings.length > 0 && (
          <div className="space-y-4">
            {readings.readings.map((r, i) => (
              <ReadingBlock key={i} reading={r} colorHex={colorHex} liturgicalColor={day.color} />
            ))}
          </div>
        )}

        {!isLoading && (error || !readings || readings.readings.length === 0) && (
          <div className="rounded-xl border border-dashed border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900">
            <div className="flex flex-col items-center py-14 text-center px-6">
              <BookOpen className="h-8 w-8 text-stone-300 dark:text-stone-600 mb-4" />
              <p className="text-stone-500 dark:text-stone-400 text-sm max-w-xs leading-relaxed">
                Readings could not be loaded. View them on the official USCCB website.
              </p>
              <a href={usccbUrl(date)} target="_blank" rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-200 underline underline-offset-4 transition-colors">
                Visit USCCB.org <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        )}
      </section>

      {/* CTA */}
      <div className="mt-12 border-t border-stone-200 dark:border-stone-700 pt-8 text-center">
        <p className="text-stone-400 dark:text-stone-500 text-lg tracking-[0.5em] mb-4 select-none">✦</p>
        <Link
          to="/ordo"
          className="inline-flex items-center gap-2 rounded-full border border-stone-300 dark:border-stone-600 px-6 py-2.5 text-sm font-semibold text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800 transition-all"
          style={{ fontFamily: '"Cinzel", serif', letterSpacing: '0.06em' }}
        >
          <BookOpen className="h-4 w-4" />
          Order of Mass
        </Link>
      </div>
    </div>
  );
}

function MetaChip({ label, value, dot }: { label: string; value: string; dot?: string }) {
  return (
    <div className="rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-3 text-center">
      <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-stone-400 dark:text-stone-500 mb-1">{label}</p>
      <p className="flex items-center justify-center gap-1.5 text-[13px] font-semibold text-stone-700 dark:text-stone-300 leading-tight">
        {dot && <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: dot }} />}
        {value}
      </p>
    </div>
  );
}

function ReadingsSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 overflow-hidden">
          <div className="flex">
            <div className="w-1 shrink-0 bg-stone-200 dark:bg-stone-700" />
            <div className="flex-1 p-5 space-y-2.5">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-4/5" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
